import { GlobalStorage } from "@otto-ec/global-resources";
import { hideLayer } from "./searchSuggest";
import { isInitialized, tracking } from "./tracking";
import { globalUtils, stringUtils } from "./util";

export type DelayedAction = (() => void) | null;

export type TrackingData = {
  "wk.san_SearchType"?: string;
  "wk.san_ActiveSearch"?: string;
  "wk.san_SuggestPos"?: string | null;
  "wk.san_SuggestSearchTerm"?: string | null;
  "wk.san_Fallback"?: string;
  "wk.san_CountHits"?: number;
  "wk.san_SearchTerm"?: string;
  "wk.san_SearchWordCount"?: number;
  san_InitialSearch?: string;
  san_Interaction?: string;
  san_SuggestSearchTermLength?: string | null;
  san_SuggestListWordCount?: string | null;
  san_SuggestList?: string | null;
  san_PageLoadComplete?: string;

  [key: string]: string | number | undefined | null;
};

export class SearchForm {
  HINT_CLASS = "hint";
  formField: HTMLFormElement;
  deleteButton: HTMLDivElement;
  searchBar: HTMLDivElement;
  searchField: HTMLInputElement;
  submitButton: HTMLDivElement;
  placeholder: string;
  errorString: string | null;
  originalSearchTerm: string | null;

  constructor(form: HTMLFormElement, delayedAction: DelayedAction = null) {
    this.formField = form;
    this.deleteButton = this.formField.getElementsByClassName("js_squirrel_searchbar__delete")[0] as HTMLDivElement;
    this.searchBar = this.formField.getElementsByClassName("js_squirrel_searchbar")[0] as HTMLDivElement;
    this.searchField = this.formField.getElementsByClassName("js_squirrel_searchbar__input")[0] as HTMLInputElement;
    this.submitButton = this.formField.getElementsByClassName("js_squirrel_searchbar__submit")[0] as HTMLDivElement;
    this.placeholder = this.searchField.placeholder || this.searchField.getAttribute("placeholder") || "";
    this.errorString = this.searchField.getAttribute("data-error");
    this.originalSearchTerm = "";

    if (Boolean(this.deleteButton).valueOf()) {
      this.toggleButtonVisibility();
      this.searchField.addEventListener("keyup", window.o_util.core.bind(this, this.toggleButtonVisibility));
      this.formField.addEventListener("reset", window.o_util.core.bind(this, this.resetHandler));
    }

    this.formField.addEventListener(
      "submit",
      ((e: SubmitEvent) => {
        e.preventDefault();
        this.submitSearch();
      }).bind(this)
    );

    this.searchField.addEventListener(
      "focus",
      (() => {
        this.clearFormField();
        this.searchField.classList.remove(this.HINT_CLASS);

        // this.searchField.parentNode.parentNode.classList.add("focus");
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const element: Element = this.searchField.parentNode.parentNode as Element;
        element.classList.add("focus");
      }).bind(this)
    );

    if (document.activeElement && document.activeElement === this.searchField) {
      this.clearFormField();
      this.searchField.classList.remove(this.HINT_CLASS);
    }

    // TODO: Elements not existing on init so they have to be later
    if (typeof delayedAction === "function") {
      delayedAction();
    }

    this.enableSearch();
  }

  trackUsage(extendByParameters: TrackingData) {
    let storage: GlobalStorage;
    let words: string | null;
    let wordLengths: string | null;

    const jsonForTracking: TrackingData = { "wk.san_SearchType": "search" };
    Object.keys(extendByParameters).forEach((key: string) => {
      jsonForTracking[key] = extendByParameters[key];
    });

    if (this.formField.classList.contains("san_track_modified")) {
      jsonForTracking["wk.san_SearchType"] = "modified";
    }
    if (this.formField.classList.contains("san_track_suggest")) {
      words = this.formField.getAttribute("data-suggestWordList");
      wordLengths = this.formField.getAttribute("data-suggestWordListCount");
      this.originalSearchTerm = this.searchField.getAttribute("data-suggest-search-term");

      jsonForTracking["wk.san_SearchType"] = "suggest";
      jsonForTracking["wk.san_SuggestSearchTerm"] = this.originalSearchTerm;
      jsonForTracking.san_SuggestSearchTermLength = this.originalSearchTerm
        ? this.originalSearchTerm.length.toString()
        : this.originalSearchTerm;
      jsonForTracking["wk.san_SuggestPos"] = this.formField.getAttribute("data-suggestpos");
      jsonForTracking.san_SuggestList = words;
      jsonForTracking.san_SuggestListWordCount = wordLengths;
    }
    jsonForTracking["wk.san_ActiveSearch"] = "true";
    jsonForTracking.san_InitialSearch = "true";
    jsonForTracking["san_Interaction"] = "initial_search";

    jsonForTracking.san_PageLoadComplete = "true";

    if (isInitialized) {
      tracking.sendTrackingDataForNextPI(jsonForTracking);
    } else {
      storage = new window.o_global.Storage(() => window.sessionStorage);
      if (storage.isAvailable) {
        jsonForTracking.san_PageLoadComplete = "false";
        try {
          storage.setItem("squirrel_searchTracking", JSON.stringify(jsonForTracking));
        } catch (e) {
          // TODO: implement error logging
        }
      }
    }
  }

  getSearchField(): HTMLInputElement {
    return this.searchField;
  }

  getValue(): string {
    return this.searchField.value;
  }

  setValue(val: string) {
    this.searchField.value = val;
  }

  setSearchFieldAttribute(key: string, val: string) {
    this.searchField.setAttribute(key, val);
  }

  isOnDefault(): boolean {
    return this.getValue() === this.errorString || this.getValue() === this.placeholder;
  }

  isEmpty(): boolean {
    return this.getValue().length < 1 || this.isOnDefault();
  }

  hideButtons() {
    this.searchBar.classList.remove("squirrel_searchbar--showButtons");
  }

  showButtons() {
    this.searchBar.classList.add("squirrel_searchbar--showButtons");
  }

  focusSearchField() {
    this.searchField.focus();
  }

  resetHandler() {
    this.hideButtons();
    this.focusSearchField();
  }

  toggleButtonVisibility() {
    if (this.isEmpty()) {
      this.hideButtons();
    } else {
      this.showButtons();
    }
  }

  clearFormField() {
    if (this.isOnDefault()) {
      this.setValue("");
    }
  }

  cleanInputField() {
    this.setValue("");
    this.searchField.focus();
    this.hideButtons();
    hideLayer();

    window.o_global.eventQBus.emit("tracking.bct.submitEvent", {
      san_Header: "search_x",
    });
  }

  fillDefaultText() {
    if (this.getValue() === "") {
      this.searchField.placeholder = this.placeholder;
      this.searchField.classList.add(this.HINT_CLASS);
    } else {
      this.searchField.classList.remove(this.HINT_CLASS);
    }
  }

  enableSearch() {
    this.searchField.removeAttribute("disabled");
    this.submitButton.removeAttribute("disabled");

    this.deleteButton.addEventListener("click", this.cleanInputField.bind(this));
    this.submitButton.addEventListener("click", this.submitSearch.bind(this));
  }

  checkAndGetIfArticleNumber(articleNumber: string): string | null {
    const regexp = /^(\.?\d{6}\w{0,2})$/g; // komischer support von führenden Punkten?
    const regexpAcht = /^([1-9]\d{7}\w{2,3})$/g;
    articleNumber = articleNumber.replace(/\s+/g, "");
    let match: string[] | null = regexp.exec(articleNumber);
    if (match) {
      return match[1];
    }
    match = regexpAcht.exec(articleNumber);
    return match ? match[1] : null;
  }

  articleNumberSearch(action: string, searchFieldValue: string): [string, TrackingData] {
    const articleNumberSearchUrl: string | null = this.formField.getAttribute("data-article-number-search");
    const articleNumber: string | null = this.checkAndGetIfArticleNumber(searchFieldValue);
    const trackingParameters: TrackingData = {};

    if (articleNumber) {
      action = articleNumberSearchUrl + "?articlenumber=" + articleNumber;
      trackingParameters["wk.san_Fallback"] = "ID";

      trackingParameters["wk.san_CountHits"] = 1;
      trackingParameters["wk.san_SearchTerm"] = articleNumber;
      trackingParameters["wk.san_SearchWordCount"] = 1;
    }
    return [action, trackingParameters];
  }

  submitSearch() {
    const searchFieldValue: string = this.searchField.value.trim();
    const target: string | null = this.formField.getAttribute("data-target");
    let action: string;
    let extendByParameters: TrackingData = {};

    if (!this.isEmpty()) {
      if (target) {
        action = target;
      } else {
        action =
          this.formField.getAttribute("action") +
          "/" +
          encodeURIComponent(stringUtils.escapeSpecials(searchFieldValue)) +
          "/";
        const [trackingAction, trackingParameters]: [string, TrackingData] = this.articleNumberSearch(
          action,
          searchFieldValue
        );
        action = trackingAction;
        extendByParameters = trackingParameters;
      }

      this.trackUsage(extendByParameters);

      globalUtils.setLocation(action);
    }
  }
}
